import { effect, Injectable, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import type { Experiment, GrowthBook, Result } from '@growthbook/growthbook';

import { OrderformFascade } from '../fascades/orderform.fascade';
import { MonitoringService } from './monitoring.service';
import { concat, defer, from, Observable, Subject } from 'rxjs';

export enum GrowthBookFeature {
  ACCESSIBILITY_TOGGLE = 'accessibility-toggle',
  AMAZON_PAY = 'amazon-pay',
  ORDERFORM_STRIKETHROUGH_PRICES = 'orderform-strikethrough-prices',
  ORDER_PROCESSING = 'order-processing',
  ORDERFORM_PROGRESS_SAVING = 'orderform-progress-saving',
  ORDERFORM_SINGLE_COLUMN_ADDRESS = 'orderform-single-column-address',
  ORDERFORM_MOBILE_FRIENDLY_INPUTS = 'orderform-mobile-friendly-inputs',
  ORDERFORM_VOUCHER_AUTOEXPAND = 'orderform-voucher-autoexpand',
  NEW_SECURITY_SEAL_ICONS = 'new-security-seal-icons',
  REFINED_ADDRESS_AUTOCOMPLETE = 'refined-address-autocomplete',
  ORDERFORM_BUY_BUTTON_LABEL_VARIANTS = 'orderform_buy_button_label_variants',
  FOOTER_WITH_LEGAL_NOTICE = 'footer-with-legal-notice',
  ORDERFORM_PAYMENT_METHODS_V2 = 'orderform-payment-methods-v2',
  EXPRESS_CHECKOUT = 'express-checkout',
  ORDERFORM_SHOW_FREE_SHIPPING = 'orderform-show-free-shipping',
}

export type BuyButtonLabelVariant =
  // v1 variants
  | 'order_now'
  | 'place_order'
  | 'pay_now'
  // v2 variants
  | 'complete_your_order'
  | 'complete_purchase'
  | 'checkout_now'
  // v3 variants
  | 'secure_checkout'
  | 'confirm_and_pay'
  | 'finish_and_pay'
  // v4 variants
  | 'purchase_now'
  | 'buy_securely'
  | 'complete_my_order';

interface GrowthBookFeatures {
  'accessibility-toggle': boolean;
  'amazon-pay': boolean;
  'orderform-strikethrough-prices': boolean;
  'order-processing': boolean;
  'orderform-progress-saving': boolean;
  'orderform-single-column-address': boolean;
  'orderform-mobile-friendly-inputs': boolean;
  'orderform-voucher-autoexpand': boolean;
  'new-security-seal-icons': boolean;
  'refined-address-autocomplete': boolean;
  'footer-with-legal-notice': boolean;
  'orderform-payment-methods-v2': boolean;
  'express-checkout': boolean;
  'orderform-show-free-shipping': boolean;
}

export interface GrowthBookVariantFeatures {
  orderform_buy_button_label_variants?: BuyButtonLabelVariant;
}
@Injectable({
  providedIn: 'root',
})
export class GrowthBookService {
  private readonly overrideParam = 'feature_override_v2';
  private readonly apiHost = 'https://cdn.growthbook.io';
  private readonly features = signal<GrowthBookFeatures>({
    'accessibility-toggle': false,
    'amazon-pay': false,
    'orderform-strikethrough-prices': false,
    'order-processing': false,
    'orderform-progress-saving': false,
    'orderform-single-column-address': false,
    'orderform-mobile-friendly-inputs': false,
    'orderform-voucher-autoexpand': false,
    'new-security-seal-icons': false,
    'refined-address-autocomplete': false,
    'footer-with-legal-notice': false,
    'orderform-payment-methods-v2': false,
    'express-checkout': false,
    'orderform-show-free-shipping': false,
  });

  private readonly variantFeatures = signal<GrowthBookVariantFeatures>({
    orderform_buy_button_label_variants: undefined,
  });

  private growthbook: GrowthBook | null = null;
  private featureOverrideCookie: string[] | null = null;
  siteowner_id = toSignal(this.orderformFacade.config$.pipe(map((config) => config.siteowner_id)));
  merchant_id = toSignal(this.orderformFacade.config$.pipe(map((config) => config.merchant_id)));
  language = this.orderformFacade.globalSettings$
    ? toSignal(this.orderformFacade.globalSettings$.pipe(map((settings) => settings.language)))
    : undefined;
  orderform_id = toSignal(this.orderformFacade.config$.pipe(map((config) => config.orderform_id)));

  #waitForInitializationResolve: () => void;
  #waitForInitialization = new Promise<void>((resolve) => {
    this.#waitForInitializationResolve = resolve;
  });
  readonly #experiments$ = new Subject<{ experiment: Experiment<any>; result: Result<any> }>();

  constructor(
    private readonly orderformFacade: OrderformFascade,
    private readonly monitoringService: MonitoringService
  ) {
    effect(
      () => {
        this.setAttributes({
          siteowner_id: this.siteowner_id(),
          merchant_id: this.merchant_id(),
          language: this.language?.(),
          orderform_id: this.orderform_id?.(),
          [this.overrideParam]: this.featureOverrideCookie,
        });
        this.updateFeatures();
      },
      { allowSignalWrites: true }
    );
  }

  async initialize(): Promise<void> {
    try {
      const clientKey = await firstValueFrom(
        this.orderformFacade.config$.pipe(
          map((config) => config.ds_global_growthbook_token),
          filter((token): token is string => !!token)
        )
      );
      const cookieValue = this.getCookie(this.overrideParam);
      this.featureOverrideCookie = cookieValue
        ? cookieValue
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : null;

      const growthbookModule = await import('@growthbook/growthbook');

      this.growthbook = new growthbookModule.GrowthBook({
        apiHost: this.apiHost,
        clientKey,
        attributes: {
          id: await this.orderformFacade.gbId,
          [this.overrideParam]: this.featureOverrideCookie,
          siteowner_id: this.siteowner_id(),
          merchant_id: this.merchant_id(),
          language: this.language?.(),
          orderform_id: this.orderform_id?.(),
        },
      });

      await this.growthbook.init();

      this.updateFeatures();
      this.#waitForInitializationResolve();
    } catch (error) {
      this.monitoringService.captureError(error);
    }
  }

  private subscribed = false;

  private ensureSubscribed(): void {
    if (this.subscribed) return;
    this.subscribed = true;

    this.growthbook.subscribe((experiment, result) => {
      if (!result.inExperiment) {
        return;
      }
      this.#experiments$.next({ experiment, result });
    });
  }

  public watchExperimentComplete(): Observable<{ experiment: Experiment<any>; result: Result<any> }> {
    return defer(() =>
      from(this.#waitForInitialization).pipe(
        tap(() => this.ensureSubscribed()),
        switchMap(() =>
          concat(
            from(Array.from(this.growthbook.getAllResults().values()).filter((result) => result.result.inExperiment)),
            this.#experiments$
          )
        )
      )
    );
  }

  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) return parts.pop().split(';').shift();

    return null;
  }

  isFeatureEnabled(featureName: keyof GrowthBookFeatures): boolean {
    return this.features()[featureName];
  }

  getFeatureVariant<K extends keyof GrowthBookVariantFeatures>(featureName: K): GrowthBookVariantFeatures[K] {
    return this.variantFeatures()[featureName];
  }

  /**
   * Get the variant features signal for reactive access
   */
  get variantFeatures$(): Signal<GrowthBookVariantFeatures> {
    return this.variantFeatures.asReadonly();
  }

  /**
   * Update order-specific attributes (e.g., product context) and re-evaluate features.
   * Use this to set attributes like has_shipping_product before feature evaluation.
   */
  public setOrderAttributes(attributes: Record<string, any>): void {
    this.setAttributes(attributes);
    this.updateFeatures();
  }

  private setAttributes(attributes: Record<string, any>): void {
    if (!this.growthbook) return;

    this.growthbook.setAttributes({
      ...this.growthbook.getAttributes(),
      ...attributes,
    });
  }

  private updateFeatures(): void {
    if (!this.growthbook) return;

    const updatedFeatures: GrowthBookFeatures = {
      'accessibility-toggle': this.growthbook.isOn(GrowthBookFeature.ACCESSIBILITY_TOGGLE),
      'amazon-pay': this.growthbook.isOn(GrowthBookFeature.AMAZON_PAY),
      'orderform-strikethrough-prices': this.growthbook.isOn(GrowthBookFeature.ORDERFORM_STRIKETHROUGH_PRICES),
      'order-processing': this.growthbook.isOn(GrowthBookFeature.ORDER_PROCESSING),
      'orderform-progress-saving': this.growthbook.isOn(GrowthBookFeature.ORDERFORM_PROGRESS_SAVING),
      'orderform-single-column-address': this.growthbook.isOn(GrowthBookFeature.ORDERFORM_SINGLE_COLUMN_ADDRESS),
      'orderform-mobile-friendly-inputs': this.growthbook.isOn(GrowthBookFeature.ORDERFORM_MOBILE_FRIENDLY_INPUTS),
      'orderform-voucher-autoexpand': this.growthbook.isOn(GrowthBookFeature.ORDERFORM_VOUCHER_AUTOEXPAND),
      'new-security-seal-icons': this.growthbook.isOn(GrowthBookFeature.NEW_SECURITY_SEAL_ICONS),
      'refined-address-autocomplete': this.growthbook.isOn(GrowthBookFeature.REFINED_ADDRESS_AUTOCOMPLETE),
      'footer-with-legal-notice': this.growthbook.isOn(GrowthBookFeature.FOOTER_WITH_LEGAL_NOTICE),
      'orderform-payment-methods-v2': this.growthbook.isOn(GrowthBookFeature.ORDERFORM_PAYMENT_METHODS_V2),
      'express-checkout': this.growthbook.isOn(GrowthBookFeature.EXPRESS_CHECKOUT),
      'orderform-show-free-shipping': this.growthbook.isOn(GrowthBookFeature.ORDERFORM_SHOW_FREE_SHIPPING),
    };

    const updatedVariantFeatures: GrowthBookVariantFeatures = {
      orderform_buy_button_label_variants: this.growthbook.getFeatureValue(
        GrowthBookFeature.ORDERFORM_BUY_BUTTON_LABEL_VARIANTS,
        undefined
      ) as BuyButtonLabelVariant,
    };

    this.features.set(updatedFeatures);
    this.variantFeatures.set(updatedVariantFeatures);
  }
}
